import { Component, DOCUMENT, inject } from '@angular/core';
import { HeadlineButtonContext } from '@ui/layout/layout.model';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { combineLatest, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ThirdLevelMenuFactory } from '@core/menu/third-level-menu.service';
import { MenuItemsModel } from '@core/menu/menu.model';
import { HeadlineButtonComponent } from '@ui/layout/headline-button/headline-button.component';
import { RouteEntity } from '@core/router/route.entity';
import { HeadlineModule } from '@ui/layout/headline/headline.module';
import { B2bProgramsState } from '../../shared/state/b2b-programs-state';
import { RxEffects } from '@rx-angular/state/effects';
import { map } from 'rxjs/operators';
import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { PartnerProgram } from '@api/models/B2bService/Contract/partner-program';
import { BtnLabels } from '@ui/models/btn.labels';
import { RoutingQueryParamsName } from '@core/router/routing.params';
import { PrivilegesModules } from '@core/privileges/privileges-modules';
import { HasPrivilege } from '@core/privileges/has-privilege';
import { XlsxService } from '@core/utils/xlsx-service';
import { UserState } from '@core/state/user.state';
import { AsyncPipe } from '@angular/common';
import { PrivilegesUsers } from '@core/privileges/privileges-users';
import { B2bProgramType } from '../../shared/models/b2b-program-type';
import { getLoadingProp } from '@core/state/operators/start-with-loading';


@Component({
    selector: 'b2b-page-program',
    templateUrl: './program.component.html',
    styleUrls: ['./program.component.scss'],
    imports: [HeadlineModule, RouterOutlet, AsyncPipe],
    providers: [RxEffects, XlsxService],
})
export class ProgramComponent {
    @HasPrivilege([PrivilegesModules.B2bLoad])
    public readonly isEditUser$: Observable<boolean>;

    readonly #effects: RxEffects = inject(RxEffects);
    readonly #route: ActivatedRoute = inject(ActivatedRoute);
    readonly #translate: TranslateService = inject(TranslateService);
    readonly #thirdLevelMenuFactory: ThirdLevelMenuFactory = inject(ThirdLevelMenuFactory);
    readonly #b2bProgramsState: B2bProgramsState = inject(B2bProgramsState);
    readonly #xlsxService: XlsxService = inject(XlsxService);
    readonly #userState: UserState = inject(UserState);
    readonly #document: Document = inject(DOCUMENT);

    public readonly pendingExport$: Observable<boolean> = this.#b2bProgramsState.loading$.pipe(
        getLoadingProp(StartWithUnit.RequestB2BProgramParticipants),
    );

    public readonly contentBtn: PolymorpheusComponent<HeadlineButtonComponent> = new PolymorpheusComponent(HeadlineButtonComponent);
    public readonly prgId: number = +this.#route.snapshot.paramMap.get(RoutingQueryParamsName.PrgId);
    public readonly menuItems$: Observable<MenuItemsModel[]> = this.prepareMenu();

    public b2bProgram: PartnerProgram;
    public headlineButtonContext: HeadlineButtonContext;

    constructor() {
        this.#effects.register(this.#route.data, (data: {
            b2bProgram: PartnerProgram;
        }) => (this.b2bProgram = data.b2bProgram));

        this.#effects.register(this.pendingExport$, (hasDownloaded: boolean) => {
            const btn: Element = this.#document.querySelector('button.btn--download');

            if (hasDownloaded) {
                btn?.setAttribute('disabled', '');
            } else {
                btn?.removeAttribute('disabled');
            }
        });

        this.#effects.register(
            this.#translate.get(BtnLabels.DownloadParticipants),
            (translateValue: string) =>
                (this.headlineButtonContext = {
                    text: '<i class="bi bi-download me-1" style="font-size: 1.2rem"></i>' + translateValue,
                    className: 'btn--download',
                    onClick: this.handlerDownloadParticipants.bind(this),
                }),
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public onActivate({ component }: { component: any }): void {
        component.isEditUser$ = this.isEditUser$;
        component.b2bProgram = this.b2bProgram;
    }

    public handlerDownloadParticipants(): void {
        this.#effects.register(this.#b2bProgramsState.requestExportParticipants(this.prgId), (response: Blob) =>
            this.#xlsxService.createXlsxFileFromBlob(response),
        );
    }

    private prepareMenu(): Observable<MenuItemsModel[]> {
        return combineLatest([
            this.#userState.checkPrivilege(PrivilegesUsers.Edit, PrivilegesModules.B2bLoad),
            this.#userState.checkPrivilege(PrivilegesUsers.Select, PrivilegesModules.B2bLoad),
            this.#route.data,
        ]).pipe(
            map(
                ([isEditB2bAccess, isSelectB2bAccess, data]: [
                    boolean,
                    boolean,
                    data: {
                        b2bProgram: PartnerProgram;
                    },
                ]) =>
                    this.#thirdLevelMenuFactory.menuItems(RouteEntity.Programs).filter((item: MenuItemsModel) => {
                        if (
                            item.mdId === 3 &&
                            ((!isEditB2bAccess && !isSelectB2bAccess) || data.b2bProgram?.prgType === B2bProgramType.NumberOfTrips)
                        ) {
                            return false;
                        }

                        item.url = item.urlTemplate.replace('#id#', encodeURIComponent(this.prgId));

                        return item;
                    }),
            ),
        );
    }

    protected readonly routeEntity: typeof RouteEntity = RouteEntity;
}
